import React, {
	memo,
	useState,
	useEffect,
	useRef,
	useMemo,
	useCallback,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useTheme, makeStyles } from '@material-ui/styles'
import { Theme, ButtonBase } from '@material-ui/core'
// Froala Editor
// Require Editor JS files.
import 'froala-editor/js/froala_editor.pkgd.min.js'
import 'froala-editor/js/plugins/align.min.js'
import 'froala-editor/js/plugins/lists.min.js'
import 'froala-editor/js/plugins/link.min.js'
import 'froala-editor/js/plugins/image.min.js'
import 'froala-editor/js/plugins/video.min.js'
import 'froala-editor/js/plugins/quick_insert.min.js'
import 'froala-editor/js/plugins/char_counter.min.js'
// Require Editor CSS files.
import 'froala-editor/css/froala_style.min.css'
import 'froala-editor/css/froala_editor.pkgd.min.css'
import 'froala-editor/css/plugins/image.min.css'
import 'froala-editor/css/plugins/video.min.css'
import 'froala-editor/css/plugins/quick_insert.min.css'
import 'froala-editor/js/plugins/char_counter.min.js'
// Require Font Awesome.
import 'font-awesome/css/font-awesome.css'
import FroalaEditor from 'react-froala-wysiwyg'
import FroalaEditorView from 'react-froala-wysiwyg/FroalaEditorView'

import { FROALA_KEY } from 'utils'
import styles from './styles'
import { useArticleState } from '../../../../reducer'
import { EArticleActionTypes, IArticleEditorProps } from 'models'

const useStyles = makeStyles(styles)

const ArticleEditor: React.FunctionComponent<IArticleEditorProps> = ({
	s3UploadHash,
	isDownSm,
	preview,
	done,
	containerRef,
}) => {
	const [isFocused, setIsFocused] = useState(false)
	const theme: Theme = useTheme()
	const classes = useStyles()
	const [maxHeight, setMaxHeight] = useState(0)
	const [{ body }, dispatch] = useArticleState()
	const handleChange = (value: string) => {
		dispatch({
			type: EArticleActionTypes.setBody,
			body: value,
		})
	}
	const { t } = useTranslation()
	let editor = useRef()
	const config = useMemo(
		() => ({
			events: {
				'edit.on': function () {
					// @ts-ignore
					if (editor && !editor.current) editor.current = this
				},
				'paste.afterCleanup': function (html: string) {
					let div = document.createElement('div')
					div.innerHTML = html
					return div.innerText
				},
				initialized: function () {
					// @ts-ignore
					if (editor && !editor.current) editor.current = this
					// @ts-ignore
					// this.events.focus()
				},
				keydown: function () {
					cancelTip()
				},
				focus: function () {
					setIsFocused(true)
				},
				blur: function () {
					setIsFocused(false)
				},
				'image.inserted': ($img: any, _: any) => {
					// $img[0].style.width = '100%'
					// const { current }: any = editor
					// current.html.set(current.html.get(false) + '<p />')
					// current.selection.setAtEnd(current.selection.endElement())
					// current.selection.restore()
					// setTimeout(
					// 	() =>
					// current.$el.get(0).lastElementChild.scrollIntoViewIfNeeded()
					// 	250,
					// )
				},
				'image.loaded': ($img: any, _: any) => {
					const { current }: any = editor
					current.image.setSize('100%', 'auto')
					current.html.set(current.html.get(false) + '<p />')
					current.selection.setAtEnd(current.selection.endElement())
					current.selection.restore()
					current.$el.get(0).lastElementChild.scrollIntoViewIfNeeded()
					setTimeout(() => {
						current.$el.get(0).lastElementChild.scrollIntoView({
							block: 'nearest',
							behavior: 'smooth',
						})
					}, 250)
				},
			},
			videoUpload: false,
			imageUploadURL: false,
			imageUploadToS3: {
				bucket: s3UploadHash?.bucket,
				keyStart: s3UploadHash?.keyStart,
				region: s3UploadHash?.region,
				params: s3UploadHash?.params,
			},
			placeholderText: t('Write your article content here'),
			charCounterCount: true,
			quickInsertEnabled: !isDownSm,
			toolbarInline: !isDownSm,
			imageInsertButtons: ['imageBack', '|', 'imageByURL', 'imageUpload'],
			toolbarButtonsXS: [
				'bold',
				'italic',
				'underline',
				'insertLink',
				'insertImage',
				'insertVideo',
			],
			pluginsEnabled: [
				'align',
				'charCounter',
				'image',
				'link',
				'lists',
				'quickInsert',
				'video',
			],
			imageEditButtons: ['imageRemove'],
			videoEditButtons: ['videoRemove'],
			key: FROALA_KEY,
			attribution: false,
			heightMax: isDownSm && !preview ? maxHeight : undefined,
			pastePlain: true,
		}),
		[editor, isDownSm, maxHeight, preview, s3UploadHash, t],
	)

	const createTooltip = useCallback(() => {
		const quickInsertEl = document.getElementsByClassName(
			'fr-quick-insert fr-visible',
		)
		let el = quickInsertEl[0].parentNode
		let tooltipWrap = document.createElement('div')
		tooltipWrap.className = classes.tooltip
		tooltipWrap.classList.add('tooltip')
		tooltipWrap.appendChild(
			document.createTextNode('Add images and format your post here'),
		)
		let node = document.createElement('p')
		node.className = classes.tooltipInfo
		node.appendChild(document.createTextNode('Got it'))
		tooltipWrap.appendChild(node)
		// @ts-ignore
		el.insertBefore(tooltipWrap, quickInsertEl[0])
	}, [classes.tooltip, classes.tooltipInfo])

	const onContainerClick = useCallback(
		frWrapper => () => {
			const frElement = document.getElementsByClassName('fr-element')
			const elementP = frElement[0].getElementsByTagName('p')
			const toolTip = document.getElementsByClassName('tooltip')
			if (elementP.length === 1) {
				if (toolTip.length === 1) {
					cancelTip()
				} else {
					const quickInsertEl = document.getElementsByClassName(
						'fr-quick-insert fr-visible',
					)
					if (quickInsertEl && quickInsertEl[0]) {
						createTooltip()
						quickInsertEl[0].addEventListener('mouseout', cancelTip)
					}
				}
			}

			if (isFocused) {
				return null
			}
			// @ts-ignore
			editor.current.events.focus(true)
			// @ts-ignore
			// editor.current.selection.setAtEnd(frWrapper)
			// @ts-ignore
			editor.current.selection.restore()
		},
		[editor, isFocused, createTooltip],
	)
	const cancelTip = () => {
		const tooltipEl = document.getElementsByClassName('tooltip')
		if (tooltipEl[0]) tooltipEl[0].remove()
	}

	useEffect(() => {
		if (!editor || !editor.current) return undefined
		// @ts-ignore
		const frWrapper = editor.current.$el.get(0)
		if (frWrapper.parentNode) {
			// @ts-ignore
			// editor.current.selection.setAtEnd(frWrapper)
			// @ts-ignore
			// editor.current.selection.restore()
			frWrapper.parentNode.addEventListener(
				'click',
				onContainerClick(frWrapper),
			)
		}
	}, [isFocused, onContainerClick])

	useEffect(() => {
		if (isDownSm) {
			setMaxHeight(window.innerHeight - 48 * 3)
		} else {
			const target: any = containerRef.current
			if (target) {
				setMaxHeight(target.getBoundingClientRect().height - 38)
			}
		}
	}, [isDownSm, containerRef])

	return preview ? (
		<div
			className={classes.preview}
			style={
				!body ? { color: theme.palette.grey[500], fontSize: 16 } : {}
			}
		>
			<FroalaEditorView
				model={body || t('Write your article content here')}
			/>
		</div>
	) : (
		(!!maxHeight && (
			<>
				<FroalaEditor
					tag="textarea"
					config={config}
					model={body}
					onModelChange={handleChange}
				/>
				{isDownSm && (
					<div className={classes.buttonsContainer}>
						<ButtonBase
							className={classes.saveButton}
							onClick={done}
						>
							{t('Done')}
						</ButtonBase>
					</div>
				)}
			</>
		)) ||
			null
	)
}

export default memo(ArticleEditor)
